import { coincideTodo } from './busqueda';
import { toast } from 'react-hot-toast';
import { direccionParaMaps } from './direccionMaps';
import { irA } from './navegacion';

export const abrirWhatsApp = (telefono, nombre, texto) => {
    if (!telefono) return toast.error("Sin teléfono");
    let num = telefono.replace(/\D/g, '');
    if (!num.startsWith('54')) num = '549' + num;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(texto || `Hola ${nombre}`)}`, '_blank');
};

export const abrirMaps = (c) => {
    const destino = `${c.calle} ${c.numero}, ${c.localidad}, Argentina`.trim();
    if (!c.calle) return toast.error("Dirección incompleta");
    irA({ direccion: destino, nombre: c.nombre });
};

// Ruta con varias paradas (Google Maps las va a mostrar en el orden dado y
// deja elegir el modo de viaje ahí mismo — sirve tanto para comparar cuál es
// mejor camino a un solo lugar como para armar una recorrida con varias
// paradas). Usado por Presupuestos (admin) y Mis Órdenes (técnico).
export function buildGoogleMapsRouteUrl(direcciones) {
    const validas = (direcciones || []).filter(d => d && d !== 'Sin dirección' && d !== 'Mostrador');
    if (validas.length === 0) return null;
    const encoded = validas.map(d => encodeURIComponent(direccionParaMaps(d)));
    return `https://www.google.com/maps/dir/${encoded.join('/')}`;
}

// Ruta del día del técnico (12-oct-2026): sale desde donde está parado y pasa por las
// visitas en el orden dado (el de la hora estimada). Formato oficial de Google Maps.
export function rutaDesdeAca(direcciones) {
    const validas = (direcciones || []).filter(d => d && d !== 'Sin dirección' && d !== 'Mostrador');
    if (validas.length === 0) return null;
    const limpias = validas.map(direccionParaMaps);
    const destino = encodeURIComponent(limpias[limpias.length - 1]);
    const paradas = limpias.slice(0, -1).map(encodeURIComponent).join('%7C');
    return `https://www.google.com/maps/dir/?api=1&destination=${destino}${paradas ? `&waypoints=${paradas}` : ''}`;
}

// Fecha corta (dd/mm/aa) usada por ClienteCard y ClienteRow -- una sola
// implementación para que no queden dos formatos de fecha distintos.
export function formatFecha(fecha) {
    if (!fecha) return null;
    // 1-nov-2026: "2026-10-30" con new Date() es la medianoche UTC y en Argentina salía un día antes (29/10)
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha).slice(0, 10));
    if (m && String(fecha).length === 10) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
    return new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// Datos derivados de un cliente que ClienteCard (mobile) y ClienteRow
// (desktop, 7-sep-2026) necesitan por igual -- centralizado acá para que
// las dos vistas no puedan desincronizarse calculando cada una lo suyo.
export function resumenCliente(cliente, sedes, equipos, servicios = []) {
    const serviciosCli = [...servicios.filter(s => (s.clienteId || s.cliente?.id) === cliente.id)]
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha) || (b.id || 0) - (a.id || 0));
    const ultimoServicio = serviciosCli[0] || null;
    const diasSinAtender = ultimoServicio
        ? Math.floor((new Date() - new Date(ultimoServicio.fecha)) / (1000 * 60 * 60 * 24))
        : null;
    const alertaSinServicio = diasSinAtender !== null && diasSinAtender > 90;
    const esEmpresa = (cliente.clienteTipo || cliente.tipo) === 'EMPRESA';
    const tieneTecnica = serviciosCli.some(s => s.servicioTipo === 'TECNICA');
    const tieneVenta   = serviciosCli.some(s => s.servicioTipo === 'VENTA');
    const iniciales = inicialesDe(cliente.nombre);
    return { serviciosCli, ultimoServicio, diasSinAtender, alertaSinServicio, esEmpresa, tieneTecnica, tieneVenta, iniciales };
}

export const filtrarClientesPorBusqueda = (clientes, sedes, equipos, busqueda) => {
    // Multi-palabra y sin acentos (3-oct-2026): "guemes cordoba" encuentra
    // "Sanatorio Güemes" con sede en Av. Córdoba. Busca en el cliente, sus sedes
    // y los N/S de sus equipos.
    if (!String(busqueda || '').trim()) return clientes;
    return clientes.filter(c => {
        const sedesCli = sedes.filter(s => (s.clienteId || s.cliente?.id) === c.id);
        const sedesId = sedesCli.map(s => String(s.id));
        const texto = [
            c.nombre, c.localidad, c.telefono, c.calle, c.numero, c.direccion, c.cuilDni,
            ...sedesCli.flatMap(s => [s.codigo, s.nombreSede, s.direccion]),
            ...equipos.filter(eq => sedesId.includes(String(eq.sedeId))).map(eq => eq.numeroSerie),
        ].filter(Boolean).join(' ');
        return coincideTodo(texto, busqueda);
    });
};


/** Dirección de una sede para el GPS ("Vera 1384, CABA"). */
export const direccionSede = (s) => (s.direccion || [[s.calle, s.numero].filter(Boolean).join(' '), s.localidad].filter(Boolean).join(', ')).trim();
export const linkMaps = (dir) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${dir}, Argentina`)}`;

/** Las sedes de un cliente que coinciden con la búsqueda (código, nombre o dirección), cuando lo que coincide
 *  es la sede y no el cliente (28-oct-2026: buscar "Vera" o "Alicia" llevaba a Modo Agua y había que buscar la
 *  sede a mano entre 52). */
export function sedesQueCoinciden(cliente, sedes, busqueda) {
    if (!String(busqueda || '').trim()) return [];
    if (coincideTodo([cliente.nombre, cliente.telefono, cliente.cuilDni].filter(Boolean).join(' '), busqueda)) return [];
    return sedes.filter(s => (s.clienteId || s.cliente?.id) === cliente.id && s.activa !== false
        && coincideTodo([s.codigo, s.nombreSede, direccionSede(s)].filter(Boolean).join(' '), busqueda));
}

// Iniciales con letras de verdad: "Ma3077 - Nicolas" → "MN" (antes salía "M-")
export function inicialesDe(nombre) {
    const palabras = String(nombre || '').split(/\s+/).filter(w => /^\p{L}/u.test(w));
    return palabras.slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
}
